import type { ImmunohistochemistryMarker, SpecialStain } from '../../../../types';

interface PathologyDetailsProps {
  diagnosis: string;
  diagnosisSummary: string;
  biopsySite: string;
  biopsyDate: string;
  surgeryDate: string;
  tumorGrade: string;
  marginStatus: string;
  cytopathologicFeatures: string[];
  immunohistochemistry: ImmunohistochemistryMarker[];
  specialStains?: SpecialStain[];
  pathologyKeywords: string[];
}

export function PathologyDetails({
  diagnosis,
  diagnosisSummary,
  biopsySite,
  biopsyDate,
  surgeryDate,
  tumorGrade,
  marginStatus,
  cytopathologicFeatures,
  immunohistochemistry,
  specialStains,
  pathologyKeywords,
}: PathologyDetailsProps) {
  const highlightText = diagnosisSummary.split('.')[0] || '';

  return (
    <div
      className="rounded-lg shadow-sm"
      style={{
        backgroundColor: 'var(--color-card)',
        border: '1px solid var(--color-border)',
        padding: '1.5rem',
      }}
    >
      {/* Report Summary Header */}
      <div
        style={{
          marginBottom: '1.5rem',
          paddingBottom: '1rem',
          borderBottom: '1px solid var(--color-border)',
        }}
      >
        <div
          className="flex items-center gap-3"
          style={{
            flexWrap: 'wrap',
          }}
        >
          <div className="flex items-center gap-2">
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              style={{ color: 'var(--color-text-muted)' }}
            >
              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
              <polyline points="14 2 14 8 20 8" />
            </svg>
            <span
              style={{
                fontSize: 'var(--font-size-lg)',
                fontWeight: '600',
                color: 'var(--color-text-primary)',
              }}
            >
              Report summary
            </span>
          </div>

          {/* Inline Diagnosis Badge - Horizontal Format */}
          <div
            style={{
              backgroundColor: '#FEF9E7',
              borderRadius: '0.375rem',
              padding: '0.5rem 0.75rem',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
            }}
          >
            <span
              style={{
                display: 'inline-block',
                width: '6px',
                height: '6px',
                borderRadius: '50%',
                backgroundColor: '#F59E0B',
                flexShrink: '0',
              }}
            />
            <span
              style={{
                fontWeight: '500',
                fontSize: 'var(--font-size-sm)',
                color: '#92400E',
              }}
            >
              {diagnosis} <span style={{ fontWeight: '400', color: '#A16207' }}>- {highlightText}</span>
            </span>
          </div>
        </div>
      </div>
      {/* Biopsy Site Header */}
      <div style={{ marginBottom: '1.5rem' }}>
        <p
          style={{
            fontSize: 'var(--font-size-xs)',
            fontWeight: '600',
            color: 'var(--color-text-muted)',
            margin: '0 0 0.25rem 0',
            textTransform: 'uppercase',
            letterSpacing: '0.5px',
          }}
        >
          Biopsy site
        </p>
        <p
          style={{
            fontSize: 'var(--font-size-sm)',
            fontWeight: '500',
            color: 'var(--color-text-primary)',
            margin: '0',
          }}
        >
          {biopsySite}
        </p>
      </div>

      {/* Biopsy Information Grid */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(2, 1fr)',
          gap: '1.5rem',
          marginBottom: '1.5rem',
        }}
      >
        {/* Left Column */}
        <div>
          <div style={{ marginBottom: '1rem' }}>
            <p
              style={{
                fontSize: 'var(--font-size-xs)',
                fontWeight: '600',
                color: 'var(--color-text-muted)',
                margin: '0 0 0.25rem 0',
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
              }}
            >
              Biopsy date
            </p>
            <p
              style={{
                fontSize: 'var(--font-size-sm)',
                fontWeight: '500',
                color: 'var(--color-text-primary)',
                margin: '0',
              }}
            >
              {biopsyDate}
            </p>
          </div>
          <div>
            <p
              style={{
                fontSize: 'var(--font-size-xs)',
                fontWeight: '600',
                color: 'var(--color-text-muted)',
                margin: '0 0 0.25rem 0',
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
              }}
            >
              Tumor grade
            </p>
            <p
              style={{
                fontSize: 'var(--font-size-sm)',
                fontWeight: '500',
                color: 'var(--color-text-primary)',
                margin: '0',
              }}
            >
              {tumorGrade}
            </p>
          </div>
        </div>

        {/* Right Column */}
        <div>
          <div style={{ marginBottom: '1rem' }}>
            <p
              style={{
                fontSize: 'var(--font-size-xs)',
                fontWeight: '600',
                color: 'var(--color-text-muted)',
                margin: '0 0 0.25rem 0',
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
              }}
            >
              Surgery date
            </p>
            <p
              style={{
                fontSize: 'var(--font-size-sm)',
                fontWeight: '500',
                color: 'var(--color-text-primary)',
                margin: '0',
              }}
            >
              {surgeryDate}
            </p>
          </div>
          <div>
            <p
              style={{
                fontSize: 'var(--font-size-xs)',
                fontWeight: '600',
                color: 'var(--color-text-muted)',
                margin: '0 0 0.25rem 0',
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
              }}
            >
              Margin status
            </p>
            <p
              style={{
                fontSize: 'var(--font-size-sm)',
                fontWeight: '500',
                color: 'var(--color-text-primary)',
                margin: '0',
              }}
            >
              {marginStatus}
            </p>
          </div>
        </div>
      </div>

      {/* Diagnosis Section */}
      <div style={{ marginBottom: '1.5rem' }}>
        <h3
          style={{
            fontSize: 'var(--font-size-base)',
            fontWeight: '600',
            color: 'var(--color-text-primary)',
            margin: '0 0 0.875rem 0',
          }}
        >
          Diagnosis
        </h3>
        <div
          style={{
            backgroundColor: '#FAFBFC',
            border: '1px solid var(--color-border)',
            borderRadius: '0.5rem',
            padding: '1.25rem',
            fontSize: 'var(--font-size-sm)',
            color: 'var(--color-text-primary)',
            lineHeight: '1.7',
          }}
        >
          {diagnosisSummary}
        </div>
      </div>

      {/* Cytopathologic Features and Immunohistochemistry */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(2, 1fr)',
          gap: '1.5rem',
          marginBottom: '1.5rem',
          paddingBottom: '1.5rem',
          borderBottom: '1px solid var(--color-border)',
        }}
      >
        {/* Cytopathologic Features - Left Column */}
        <div>
          <h3
            style={{
              fontSize: 'var(--font-size-base)',
              fontWeight: '600',
              color: 'var(--color-text-primary)',
              margin: '0 0 0.875rem 0',
            }}
          >
            Cytopathologic Features
          </h3>
          <ul style={{ listStyle: 'none', padding: '0', margin: '0' }}>
            {cytopathologicFeatures.map((feature, index) => (
              <li
                key={index}
                style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: '0.5rem',
                  marginBottom: '0.5rem',
                }}
              >
                <span
                  style={{
                    display: 'inline-block',
                    width: '6px',
                    height: '6px',
                    borderRadius: '50%',
                    backgroundColor: '#9CA3AF',
                    marginTop: '6px',
                    flexShrink: '0',
                  }}
                />
                <span
                  style={{
                    fontSize: 'var(--font-size-sm)',
                    color: 'var(--color-text-primary)',
                    lineHeight: '1.5',
                  }}
                >
                  {feature}
                </span>
              </li>
            ))}
          </ul>
        </div>

        {/* Immunohistochemistry - Right Column */}
        <div>
          <h3
            style={{
              fontSize: 'var(--font-size-base)',
              fontWeight: '600',
              color: 'var(--color-text-primary)',
              margin: '0 0 0.875rem 0',
            }}
          >
            Immunohistochemistry
          </h3>
          <ul style={{ listStyle: 'none', padding: '0', margin: '0' }}>
            {immunohistochemistry.map((marker, index) => (
              <li
                key={index}
                style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: '0.5rem',
                  marginBottom: '0.5rem',
                }}
              >
                <span
                  style={{
                    display: 'inline-block',
                    width: '6px',
                    height: '6px',
                    borderRadius: '50%',
                    backgroundColor: marker.status === 'positive' ? '#22C55E' : '#EF4444',
                    marginTop: '6px',
                    flexShrink: '0',
                  }}
                />
                <span
                  style={{
                    fontSize: 'var(--font-size-sm)',
                    color: 'var(--color-text-primary)',
                    lineHeight: '1.5',
                  }}
                >
                  <strong>{marker.marker}:</strong> {marker.result}
                </span>
              </li>
            ))}
          </ul>
        </div>
      </div>

      {/* Special Stains - Horizontal Layout */}
      {specialStains && specialStains.length > 0 && (
        <div style={{ marginBottom: '1.5rem' }}>
          <h3
            style={{
              fontSize: 'var(--font-size-base)',
              fontWeight: '600',
              color: 'var(--color-text-primary)',
              margin: '0 0 0.875rem 0',
            }}
          >
            Special Stains
          </h3>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
              gap: '1rem',
            }}
          >
            {specialStains.map((stain, index) => (
              <div key={index}>
                <h4
                  style={{
                    fontSize: 'var(--font-size-sm)',
                    fontWeight: '600',
                    color: 'var(--color-text-primary)',
                    margin: '0 0 0.25rem 0',
                  }}
                >
                  {stain.stain}
                </h4>
                <p
                  style={{
                    fontSize: 'var(--font-size-sm)',
                    color: 'var(--color-text-secondary)',
                    margin: '0',
                    lineHeight: '1.5',
                  }}
                >
                  {stain.finding}
                </p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Pathology Interpretation Keywords */}
      <div>
        <h3
          style={{
            fontSize: 'var(--font-size-base)',
            fontWeight: '600',
            color: 'var(--color-text-primary)',
            margin: '0 0 0.875rem 0',
          }}
        >
          Pathology interpretation keywords
        </h3>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
          {pathologyKeywords.map((keyword, index) => (
            <span
              key={index}
              style={{
                padding: '0.375rem 0.75rem',
                borderRadius: '0.375rem',
                fontSize: 'var(--font-size-sm)',
                fontWeight: '500',
                backgroundColor: '#DBEAFE',
                color: '#1E40AF',
              }}
            >
              {keyword}
            </span>
          ))}
        </div>
      </div>
    </div>
  );
}
